/**
 * SidebarNav
 * -----------
 * The primary left-rail navigation for authenticated users.
 *
 * Responsive behavior is driven by the caller (`Nav.tsx`), which decides
 * whether to render this as a full sidebar, a collapsed rail, or inside a
 * mobile drawer. This component only cares about two orthogonal inputs:
 *   - `variant`: "full" | "rail"       (visual size)
 *   - `onNavigate`: optional callback   (so the mobile drawer can close itself)
 *
 * Role logic (Staff / RDP / CBL Disputes) is preserved verbatim from the old
 * monolithic Nav — see the tab-composition block below.
 *
 * Badges: hand-rolled SVG red circles are replaced with the shadcn `Badge`
 * component. We alias variant `destructive` as `DangerBadge` so that, once
 * SWE-1 publishes the `danger` variant, the swap is a single line.
 */
import React from "react";
import _ from "lodash";
import { Link, useParams } from "@tanstack/react-router";
import {
  HardHatIcon,
  HomeIcon,
  UserIcon,
  FileTextIcon,
  LineChartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  type LucideIcon,
} from "lucide-react";
import BuildingIcon from "@/components/icons/Building";
import GearIcon from "@/components/icons/Gear";
import {
  useMeQuery,
  useRdpSubmissionsQuery,
  useStaffSubmissionsQuery,
  useCBLDisputesQuery,
} from "@/queries/queries";
import { Badge } from "@/components/shared/ui/badge";
import {
  Tooltip,
  TooltipProvider,
} from "@/components/shared/ui/tooltip";
// NOTE: we intentionally do NOT import navigation-menu here — we swapped to
// semantic <nav><ul><li> so the a11y tree matches the visible structure.
import { cn } from "@/lib/utils";
import { ImpersonationMenuButton } from "@/components/common/ImpersonationMenu";

// TODO(SWE-1): swap to <Badge variant="danger"> once the extended variants ship.
const DangerBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Badge
    variant="destructive"
    className="ml-auto min-w-[1.25rem] justify-center px-1.5"
    aria-label={`${children} items need attention`}
  >
    {children}
  </Badge>
);

interface NavTab {
  label: string;
  linkTo: string;
  icon: LucideIcon | React.FC<{ size?: number; color?: string }>;
  badgeCount?: number;
}

export type SidebarVariant = "full" | "rail";

interface SidebarNavProps {
  variant: SidebarVariant;
  /**
   * Optional — used by the mobile sheet to close itself after a link click,
   * and by the desktop collapse toggle to flip between full/rail.
   */
  onNavigate?: () => void;
  onToggleVariant?: () => void;
  /** Hide the collapse toggle (mobile drawer doesn't need one). */
  showToggle?: boolean;
}

const SidebarNav: React.FC<SidebarNavProps> = ({
  variant,
  onNavigate,
  onToggleVariant,
  showToggle = true,
}) => {
  const { data: user } = useMeQuery();
  // Params are only needed when role logic depends on them. The old Nav also
  // read building/submission params for the contextual submenu — that
  // submenu has moved to ContextualPageNav, so we no longer pull it in here.
  // We keep `useParams` out to avoid an unnecessary re-render source.
  useParams({ strict: false });

  const { data: staffSubmissionsData } = useStaffSubmissionsQuery(user);
  const { data: rdpSubmissionsData } = useRdpSubmissionsQuery(user);
  const { data: cblDisputesData } = useCBLDisputesQuery();

  if (!user) return null;

  // --- Staff badge: submissions in review (700) or awaiting-payment (850).
  let combinedSubmissions = (staffSubmissionsData?.ll97Submissions ||
    []) as any[];
  combinedSubmissions = combinedSubmissions.concat(
    staffSubmissionsData?.ll88Submissions || [],
  );
  combinedSubmissions = combinedSubmissions.concat(
    staffSubmissionsData?.cblDisputes?.map((cbl) => cbl.ll97Submissions) || [],
  );
  combinedSubmissions = combinedSubmissions.filter(
    (s) => s.status === 700 || s.status === 850,
  );
  combinedSubmissions = _.uniqBy(combinedSubmissions, (s) => s.id) as any[];
  const staffSubmissionsCount = combinedSubmissions?.length || 0;

  // --- RDP badge: submissions at RDP-review (800).
  const rdpSubmissionsCount =
    (rdpSubmissionsData?.ll97Submissions.filter((s) => s.status === 800)
      .length || 0) +
    (rdpSubmissionsData?.ll88Submissions.filter((s) => s.status === 800)
      .length || 0);

  // --- CBL Disputes badge: open (0) or response-needed (6).
  const cblDisputesBadgeCount =
    cblDisputesData?.filter(
      (d) => d.cblDisputeStatus === 0 || d.cblDisputeStatus === 6,
    ).length || 0;

  // --- Compose tabs. Preserve all role-gated branches from old Nav.
  const tabs: NavTab[] = [
    { label: "Dashboard", linkTo: "/", icon: HomeIcon },
    { label: "Buildings", linkTo: "/buildings", icon: BuildingIcon },
  ];

  // Tracking entry is staff-only on the sidebar (Leo, May 4, 2026):
  // even non-staff users in the `tracking_beta` Django Group should not
  // see the Tracking nav item. The server-side IsTrackingEnabled gate
  // (which still permits tracking_beta members for the API) is left
  // alone — this is a UI hide, not a permissions change. If we ever
  // want non-staff customers to see Tracking, gate on a separate
  // explicit flag rather than re-coupling to ``trackingEnabled``.
  //
  // Wave 2 deferred: gate also accepts user.company?.platform_home === "portal"
  // once Portal is validated by staff. Per Leo (2026-05-05), staff-only for now.
  if (user.isStaff) {
    tabs.push({
      label: "Tracking",
      linkTo: "/portal",
      icon: LineChartIcon,
    });
  }

  if (user.rdpInfo?.id) {
    tabs.push({
      label: "RDP",
      linkTo: "/rdp",
      icon: HardHatIcon,
      badgeCount: rdpSubmissionsCount,
    });
  }
  if (user.isStaff) {
    tabs.push({
      label: "Staff",
      linkTo: "/staff",
      icon: UserIcon,
      badgeCount: staffSubmissionsCount,
    });
  }
  if (cblDisputesData && cblDisputesData.length > 0) {
    tabs.push({
      label: "CBL Disputes",
      linkTo: "/cbl_disputes",
      icon: FileTextIcon,
      badgeCount: cblDisputesBadgeCount,
    });
  }
  tabs.push({ label: "Settings", linkTo: "/settings", icon: GearIcon });

  const isRail = variant === "rail";

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className={cn(
          "flex h-full flex-col bg-primary/5",
          isRail ? "items-center px-2 py-4" : "px-4 py-4",
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            "flex items-center pb-8",
            isRail ? "justify-center" : "justify-center pt-1",
          )}
        >
          <img
            src="/codeClearLogo.svg"
            alt="CodeClear"
            className={cn("object-contain", isRail ? "h-7 w-7" : "h-8")}
          />
        </div>

        {/* Primary nav */}
        <nav aria-label="Primary" className="flex-1">
          <ul
            className={cn(
              "flex flex-col gap-1",
              isRail ? "items-center" : "items-stretch",
            )}
          >
            {tabs.map((tab) => (
              <li key={tab.linkTo} className={isRail ? "" : "w-full"}>
                <Link
                  to={tab.linkTo}
                  onClick={onNavigate}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isRail
                      ? "h-11 w-11 justify-center"
                      : "w-full px-3 py-2 tracking-tight",
                    "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                  activeProps={{
                    className: "!bg-primary !text-primary-foreground",
                  }}
                >
                  {({ isActive }) => {
                    const iconEl = (
                      <tab.icon
                        size={20}
                        color={isActive ? "currentColor" : undefined}
                        aria-hidden="true"
                      />
                    );
                    const labelEl = (
                      <span className="truncate text-sm font-medium">
                        {tab.label}
                      </span>
                    );
                    const badgeEl =
                      !!tab.badgeCount && tab.badgeCount > 0 ? (
                        <DangerBadge>{tab.badgeCount}</DangerBadge>
                      ) : null;

                    if (isRail) {
                      // shared/ui/tooltip re-exports a wrapper whose API is
                      // <Tooltip content="...">{trigger}</Tooltip>. Using the
                      // Radix-style nested children here produced a bare
                      // DOM-rendered <TooltipContent> floating outside the
                      // portal — the "bug dialog" seen on rail hover.
                      return (
                        <Tooltip content={tab.label}>
                          <span
                            className="relative inline-flex"
                            aria-label={tab.label}
                          >
                            {iconEl}
                            {!!tab.badgeCount && tab.badgeCount > 0 && (
                              <span
                                className="absolute -right-1 -top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground"
                                aria-label={`${tab.badgeCount} need attention`}
                              >
                                {tab.badgeCount}
                              </span>
                            )}
                          </span>
                        </Tooltip>
                      );
                    }
                    return (
                      <>
                        {iconEl}
                        {labelEl}
                        {badgeEl}
                      </>
                    );
                  }}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Staff impersonation (dev-only endpoint on the backend). */}
        {user.isStaff && (
          <div
            className={cn(
              "mt-4",
              isRail ? "flex justify-center" : "px-1",
            )}
          >
            <ImpersonationMenuButton userIsStaff compact={isRail} />
          </div>
        )}

        {/* Collapse toggle (hidden inside mobile sheet). */}
        {showToggle && onToggleVariant && (
          <button
            type="button"
            onClick={onToggleVariant}
            aria-label={isRail ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isRail}
            className={cn(
              "mt-4 inline-flex items-center gap-2 rounded-xl text-muted-foreground",
              "hover:text-foreground hover:bg-muted transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isRail
                ? "h-9 w-9 justify-center self-center"
                : "w-full px-3 py-2 text-sm",
            )}
          >
            {isRail ? (
              <ChevronRightIcon size={18} aria-hidden="true" />
            ) : (
              <>
                <ChevronLeftIcon size={18} aria-hidden="true" />
                <span className="tracking-tight">Collapse</span>
              </>
            )}
          </button>
        )}

        {/* NO BUILD STAMP HERE, deliberately. This is the CLASSIC/Filing
            chrome, and the tracker-only scope rule (Leo, 2026-09-07:
            "tracker only for always") puts it out of bounds — the component
            itself is in scope, mounting it into this shell is not. The stamp
            lives in components/portal/TrackerSidebar.tsx, which is the
            tracker's own chrome. It is also staff-gated there, so the two
            rules compose rather than overlap: scope decides WHICH shell,
            is_staff decides WHO. Do not re-add it here to "keep the two
            sidebars consistent" — that consistency is the violation. */}
      </div>
    </TooltipProvider>
  );
};

export default SidebarNav;
